import { useState, useEffect, useCallback, useRef } from "react"

export const useElementObserver = (
  targetRef: {
    current: HTMLDivElement | null
  },
  options?: {
    intersectionOffset?: number
    threshhold?: number
  }
) => {
  const [isVisible, setIsVisible] = useState<boolean>()
  const [intersectionRatio, setIntersectionRatio] = useState<number>(0)
  const observerRef = useRef<IntersectionObserver>()
  const startObservingIntervalId = useRef<NodeJS.Timeout>()
  const alreadyObserving = useRef<boolean>()

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: `${options?.intersectionOffset || 0}px`,
      threshold: options?.threshhold || 0,
    }

    observerRef.current = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry: IntersectionObserverEntry) => {
          if (entry.isIntersecting) {
            setIntersectionRatio(entry.intersectionRatio)
            setIsVisible(true)
          } else {
            setIntersectionRatio(0)
            setIsVisible(false)
          }
        })
      },
      observerOptions
    )

    return () => {
      if (observerRef.current) {
        observerRef.current?.disconnect()
      }

      if (startObservingIntervalId.current) {
        clearInterval(startObservingIntervalId.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRef, observerRef])

  const startObserving = useCallback(() => {
    if (alreadyObserving.current) {
      return
    }

    startObservingIntervalId.current = setInterval(() => {
      if (targetRef && targetRef.current && observerRef.current) {
        observerRef.current.observe(targetRef.current)
        alreadyObserving.current = true
        clearInterval(startObservingIntervalId.current)
      }
    }, 100)
  }, [observerRef, targetRef])

  return { isVisible, intersectionRatio, startObserving }
}

export const useMultipleElementsObserver = (
  targetRefs: {
    current: Map<string, HTMLDivElement> | null
  },
  options?: {
    intersectionOffset?: number
    threshhold?: number
  }
) => {
  const [visibleElement, setVisibleElement] = useState<HTMLDivElement>()
  const observerRef = useRef<IntersectionObserver>()
  const startObservingIntervalId = useRef<NodeJS.Timeout>()
  const alreadyObserving = useRef<boolean>()

  useEffect(() => {
    const threshhold = options?.threshhold || 0
    const observerOptions = {
      root: null,
      rootMargin: `${options?.intersectionOffset || 0}px`,
      threshold: threshhold,
    }

    observerRef.current = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry: IntersectionObserverEntry) => {
          const anchorId = entry.target.getAttribute("id")

          if (entry.isIntersecting) {
            if (anchorId) {
              setVisibleElement(entry.target as HTMLDivElement)
              //TODO need a method to set visibleElement to undefined when none of the targetElements are visible
            }
          }
        })
      },
      observerOptions
    )

    return () => {
      if (observerRef.current) {
        observerRef.current?.disconnect()
      }

      if (startObservingIntervalId.current) {
        clearInterval(startObservingIntervalId.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRefs, observerRef])

  const startObserving = useCallback(() => {
    if (alreadyObserving.current) {
      return
    }

    startObservingIntervalId.current = setInterval(() => {
      if (targetRefs && targetRefs && observerRef.current) {
        targetRefs.current?.forEach((targetRef) => {
          if (targetRef) {
            observerRef.current?.observe(targetRef)
          }
        })
        alreadyObserving.current = true
        clearInterval(startObservingIntervalId.current)
      }
    }, 100)
  }, [observerRef, targetRefs])

  return { visibleElement, startObserving }
}
